import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperButtonComponent } from '@ricoh-paper/angular';
import { filter, map } from 'rxjs';

import { MeetingPanelService } from '../../../core/services/meeting-panel.service';
import { MeetingPanelFlagService } from '../../../core/services/meeting-panel-flag.service';
import { isScheduleRoute } from '../../../core/utils/schedule-link';
import { SchedulePreviewComponent } from '../schedule-preview.component';
import { SCHEDULE_PANEL_ONLY } from '../state/meeting-panel';

/** A URL's first path segment ("home", "map", "preview"): the page, without its query or fragment. */
function pageSegment(url: string): string {
  return url.split('?')[0].split('#')[0].split('/').filter(Boolean)[0] ?? '';
}

/** How long the panel waits for its meeting before it says it could not open it. */
const OPEN_TIMEOUT_MS = 15_000;

/**
 * im-meeting-panel-host — the schedule's meeting panel over ANY page.
 *
 * ARCHITECTURE
 *   - Mounted once in the app shell. While `MeetingPanelService` has a request
 *     and the route is not /schedule (which opens requests in place), it
 *     mounts the schedule page PANEL-ONLY (`SCHEDULE_PANEL_ONLY`): the same
 *     card, editor, sizes and Escape ladder, with the grid hidden.
 *   - The page is `@defer`red, so the schedule's code loads on the first open
 *     and never weighs on the app's first load; and it is inside the `@if`, so
 *     closing the panel destroys it — its data service, polling, realtime feed
 *     and shortcuts with it. Nothing of the schedule runs here while shut.
 *   - THE FIRST OPEN ANSWERS AT ONCE: until the page shows the meeting
 *     (`loading`), this draws the panel's frame in the card's shape — small for
 *     details, the docked editor's width for edit — with a loading state, and
 *     says so plainly if the meeting cannot be opened. Never a dead click.
 *   - A bridge: when the editor's state moves out of the page
 *     (indoor_mapping_poc-e4sb.2), this renders the panel directly and the
 *     service's callers do not change.
 */
@Component({
  selector: 'im-meeting-panel-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SchedulePreviewComponent, TranslocoPipe, PaperButtonComponent],
  providers: [{ provide: SCHEDULE_PANEL_ONLY, useValue: true }],
  host: { class: 'ph-no-capture', '(document:keydown.escape)': 'onEscape($event)' },
  template: `
    @if (show()) {
      @if (panel.loading()) {
        <section
          class="mp-frame"
          [class.mp-frame--large]="large()"
          [class.mp-frame--refresh]="refresh.on()"
          [class.mp-frame--stands-in]="!refresh.on() && !!panel.back()"
          data-testid="meeting-panel-loading"
          data-companion-avoid
          role="status"
          aria-live="polite"
          [attr.aria-busy]="failed() ? null : 'true'"
        >
          @if (failed()) {
            <p class="mp-failed">{{ 'schedule.meeting_panel.open_failed' | transloco }}</p>
            <!-- A failed read and a meeting that is gone look the same from
                 here, so a passing failure gets its Try again. -->
            <div class="mp-actions">
              <paper-button intent="primary" data-testid="meeting-panel-retry" (click)="retry()">
                {{ 'schedule.meeting_panel.try_again' | transloco }}
              </paper-button>
              <paper-button intent="secondary" data-testid="meeting-panel-loading-close" (click)="panel.close()">
                {{ 'schedule.meeting_panel.close' | transloco }}
              </paper-button>
            </div>
          } @else {
            <span class="mp-sr">{{ 'schedule.meeting_panel.opening' | transloco }}</span>
            <span class="mp-bar mp-bar--title" aria-hidden="true"></span>
            <span class="mp-bar mp-bar--line" aria-hidden="true"></span>
            <span class="mp-bar mp-bar--line mp-bar--short" aria-hidden="true"></span>
            <span class="mp-bar mp-bar--line" aria-hidden="true"></span>
          }
        </section>
      }
      @defer (on immediate) {
        <im-schedule-preview />
      }
    }
  `,
  styleUrl: './meeting-panel-host.component.scss',
})
export class MeetingPanelHostComponent {
  protected readonly panel = inject(MeetingPanelService);
  /** The refresh (`schedule-meeting-panel`): off, the frame and the page-change close are main's. */
  protected readonly refresh = inject(MeetingPanelFlagService);
  private readonly router = inject(Router);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /** On /schedule the page itself opens the request; everywhere else, this. */
  protected readonly show = computed(() => this.panel.isOpen() && !isScheduleRoute(this.url() ?? ''));
  protected readonly large = computed(() => this.panel.request()?.section === 'edit' || (this.refresh.on() && !!this.panel.request()?.create));
  private readonly timedOut = signal(false);
  /** The page found nothing to show, or the schedule never answered. */
  protected readonly failed = computed(() => this.panel.openError() !== null || this.timedOut());

  /** Ask again for the same meeting, the same way: a new request, with its own wait. */
  protected retry(): void {
    const request = this.panel.request();
    if (!request) return;
    if (request.create) {
      this.panel.openCreate(request.create, {
        size: request.size,
        ...(request.back ? { back: request.back } : {}),
        ...(request.askFresh === false ? { askFresh: false } : {}),
      });
      return;
    }
    // An open from an Ask answer keeps that answer's chat on the retry too.
    this.panel.open(request.target, {
      section: request.section, size: request.size, ...(request.askFresh === false ? { askFresh: false } : {}),
      ...(request.tab ? { tab: request.tab } : {}),
      ...(request.back ? { back: request.back } : {}),
    });
  }

  /** Escape while the meeting is still on its way (or failed to come) cancels the open. */
  protected onEscape(event: Event): void {
    if (event.defaultPrevented || !this.show() || !this.panel.loading()) return;
    event.preventDefault();
    this.panel.close();
  }

  /** The page (its first path segment) the open request was opened over. */
  private requestPage: { readonly seq: number; readonly segment: string } | null = null;

  constructor() {
    // THE PAGE CHANGED UNDER THE PANEL (a nav link, a shortcut, an Ask answer that navigates). Off /schedule the
    // panel stands in for the page it opened over: its Back bar names that page and its history entry sits on
    // it. Left open over the next page it would say "Back to Home" over the Map. It closes instead, without
    // popping (the router owns the stack now); a dirty editor is written to Drafts as the page goes. Moving to
    // /schedule keeps it: the schedule opens the same request in place. A follow-on request (a retry, a second
    // door) keeps the page the first one opened over.
    effect(() => {
      const url = this.url() ?? '';
      const request = this.panel.request();
      untracked(() => {
        if (!request) {
          this.requestPage = null;
          return;
        }
        const segment = pageSegment(url);
        if (this.requestPage?.seq !== request.seq) {
          this.requestPage = { seq: request.seq, segment: this.requestPage?.segment ?? segment };
          return;
        }
        if (this.refresh.on() && request.back && segment !== this.requestPage.segment && !isScheduleRoute(url)) {
          this.panel.closeForNavigation();
        }
      });
    });

    // Loading too long means the meeting did not open: say so, offer Close.
    // Each request has its own wait, so Try again starts the clock afresh.
    effect((onCleanup) => {
      const waiting = this.show() && this.panel.loading();
      this.panel.request();
      untracked(() => this.timedOut.set(false));
      if (!waiting) return;
      const timer = setTimeout(() => this.timedOut.set(true), OPEN_TIMEOUT_MS);
      onCleanup(() => clearTimeout(timer));
    });
  }
}
